import { DocumentV2, ImageRotationDegrees, RotateImageRequest } from '@eigtech/documents-types'
import { useRotateImage } from '@eigtech/ui-shared-documents'
import { useControllableState } from '@eigtech/ui/hooks/use-controllable-state'
import { useCallback } from 'react'

export function useRotatePhotoState(
  document: DocumentV2,
  {
    rotationDegrees: propsRotationDegrees,
    setRotationDegrees: propsSetRotationDegrees,
  }: {
    rotationDegrees?: 0 | ImageRotationDegrees
    setRotationDegrees?: (degrees: 0 | ImageRotationDegrees) => void
  } = {}
) {
  const [rotationDegrees, setRotationDegrees] = useControllableState<0 | ImageRotationDegrees>({
    value: propsRotationDegrees,
    defaultValue: 0,
    onChange: propsSetRotationDegrees,
  })

  const saveRotateEnabled = rotationDegrees !== 0

  const {
    mutateAsync: rotateImage,
    isError,
    isPending,
    isIdle,
    isPaused,
    isSuccess,
    data,
    error,
  } = useRotateImage()

  const rotateLeft = useCallback(
    () => setRotationDegrees(((rotationDegrees + 270) % 360) as ImageRotationDegrees),
    [rotationDegrees, setRotationDegrees]
  )

  const rotateRight = useCallback(() => {
    setRotationDegrees(((rotationDegrees + 90) % 360) as ImageRotationDegrees)
  }, [rotationDegrees, setRotationDegrees])

  const saveRotate = useCallback(async () => {
    if (rotationDegrees === 0) return

    const payload: RotateImageRequest = {
      documentCorn: `corn:documents:document:${document.id}`,
      entityId: document.entityId,
      rotation: {
        degrees: rotationDegrees,
      },
    }

    await rotateImage(payload)
    setRotationDegrees(0)
  }, [document.entityId, document.id, rotateImage, rotationDegrees, setRotationDegrees])

  return {
    saveRotateEnabled,
    rotateLeft,
    rotateRight,
    saveRotate,
    isError,
    isPending,
    isIdle,
    isPaused,
    isSuccess,
    data,
    error,
    rotationDegrees,
    setRotationDegrees,
  }
}
